<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import Tag from 'primevue/tag'
import api from '@/api'
import { useI18n } from '@/i18n'
import { useFormatDate } from '@/composables/useFormatDate'
import { flagFromISO2 } from '@/utils/country'
import { useBreadcrumbStore } from '@/stores/breadcrumb'

const route = useRoute()
const { t } = useI18n()
const { fmtDate } = useFormatDate()
const breadcrumbStore = useBreadcrumbStore()

const group   = ref(null)
const loading = ref(true)
const error   = ref('')

const fetchGroup = async () => {
  try {
    const response = await api.getHackerGroupById(route.params.id)
    group.value = response.data
    breadcrumbStore.setTitle(group.value?.name)
  } catch (err) {
    error.value = t('hackwatch.groupDetail.overviewError')
  } finally {
    loading.value = false
  }
}

const formatMotive = (motive) => {
  if (!motive) return null
  if (Array.isArray(motive)) return motive.join(', ')
  if (typeof motive === 'string') return motive
  return JSON.stringify(motive)
}

onMounted(() => { fetchGroup() })
</script>

<template>
  <div class="hw-card">
    <div class="hw-card-header">
      <i class="pi pi-info-circle hw-card-icon" />
      <span class="hw-card-title">{{ group?.name ?? t('hackwatch.groupDetail.legendOverview') }}</span>
    </div>

    <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>

    <div v-else class="hw-card-body">
      <div v-if="error" class="detail-error-card">
        <i class="pi pi-exclamation-triangle detail-error-icon" />
        <p class="detail-error-message">{{ error }}</p>
        <pButton :label="t('common.refresh')" icon="pi pi-refresh"
          severity="secondary" outlined size="small" @click="fetchGroup" />
      </div>

      <div v-else-if="group" class="overview-grid">

      <!-- Name + RaaS-Badge -->
      <div class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelName') }}</span>
        <span class="overview-value">
          <strong>{{ group.name }}</strong>
          <Tag v-if="group.is_raas" :value="t('hackwatch.groupDetail.labelRaas')"
            severity="danger" style="font-size: 0.7rem; padding: 0.1rem 0.4rem; margin-left: 0.4rem;" />
        </span>
      </div>

      <!-- Beschreibung -->
      <div v-if="group.description" class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelDescription') }}</span>
        <span class="overview-value">{{ group.description }}</span>
      </div>

      <!-- Land -->
      <div v-if="group.country" class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelCountry') }}</span>
        <span class="overview-value flex align-items-center gap-1">
          <span v-if="flagFromISO2(group.country)" style="font-size: 1.2rem; line-height: 1;">{{ flagFromISO2(group.country) }}</span>
          <span>{{ group.country }}</span>
        </span>
      </div>

      <!-- Staatl. Sponsor -->
      <div v-if="group.state_sponsor" class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelStateSponsor') }}</span>
        <span class="overview-value">{{ group.state_sponsor }}</span>
      </div>

      <!-- Attribution Confidence -->
      <div v-if="group.attribution_confidence" class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelAttributionConfidence') }}</span>
        <span class="overview-value">{{ group.attribution_confidence }}</span>
      </div>

      <!-- Opfer -->
      <div v-if="group.victim_count != null" class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelVictimCount') }}</span>
        <span class="overview-value">{{ group.victim_count.toLocaleString() }}</span>
      </div>

      <!-- Erstmals gesehen -->
      <div v-if="group.first_seen" class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelFirstSeen') }}</span>
        <span class="overview-value">{{ fmtDate(group.first_seen) }}</span>
      </div>

      <!-- Motiv -->
      <div v-if="formatMotive(group.motive)" class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelMotive') }}</span>
        <span class="overview-value">{{ formatMotive(group.motive) }}</span>
      </div>

      <!-- Zielbranchen -->
      <div v-if="group.targeted_sectors?.length" class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelTargetedSectors') }}</span>
        <span class="overview-value">
          <Tag v-for="sector in (Array.isArray(group.targeted_sectors) ? group.targeted_sectors : [])"
            :key="sector" :value="sector" severity="secondary"
            style="font-size: 0.72rem; margin: 0.1rem 0.2rem 0.1rem 0;" />
        </span>
      </div>

      <!-- Malware-Familien (Malpedia) -->
      <div v-if="group.families?.length" class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelFamilies') }}</span>
        <span class="overview-value">
          <Tag v-for="family in (Array.isArray(group.families) ? group.families : [])"
            :key="family" :value="family" severity="warn"
            style="font-size: 0.72rem; margin: 0.1rem 0.2rem 0.1rem 0;" />
        </span>
      </div>

      <!-- MITRE ATT&CK Group -->
      <div v-if="group.mitre_group_id" class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelMitreGroup') }}</span>
        <span class="overview-value">
          <a :href="`https://attack.mitre.org/groups/${group.mitre_group_id}/`"
            target="_blank" rel="noopener noreferrer" class="hw-link">
            {{ group.mitre_group_id }}
            <i class="pi pi-external-link" style="font-size: 0.75rem; margin-left: 0.25rem;" />
          </a>
        </span>
      </div>

      <!-- Malpedia-Link -->
      <div v-if="group.malpedia_name" class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelMalpedia') }}</span>
        <span class="overview-value">
          <a :href="`https://malpedia.caad.fkie.fraunhofer.de/actor/${group.malpedia_name}`"
            target="_blank" rel="noopener noreferrer" class="hw-link">
            {{ group.malpedia_name }}
            <i class="pi pi-external-link" style="font-size: 0.75rem; margin-left: 0.25rem;" />
          </a>
        </span>
      </div>

      <!-- Erstellt / Aktualisiert -->
      <div class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelCreated') }}</span>
        <span class="overview-value">{{ fmtDate(group.created_at) }}</span>
      </div>
      <div class="overview-row">
        <span class="overview-label">{{ t('hackwatch.groupDetail.labelUpdated') }}</span>
        <span class="overview-value">{{ fmtDate(group.updated_at) }}</span>
      </div>

    </div>

      <div v-else class="muted">{{ t('hackwatch.groupDetail.overviewNotFound') }}</div>
    </div>
  </div>
</template>

<style scoped>
.muted { opacity: .65; }
.overview-grid { display: flex; flex-direction: column; gap: .35rem; }
.overview-row  { display: grid; grid-template-columns: 160px 1fr; gap: .5rem; padding: .3rem 0; border-bottom: 1px solid var(--p-content-border-color); align-items: start; }
.overview-row:last-child { border-bottom: none; }
.overview-label { font-weight: 600; font-size: .85rem; opacity: .7; padding-top: .15rem; }
.overview-value { font-size: .9rem; word-break: break-word; }
.hw-link { color: var(--p-primary-color); text-decoration: none; }
.hw-link:hover { text-decoration: underline; }
</style>
